import { MeditationSessionState } from "./meditationChannel";
import { BackgroundTrackId, Meditation, MeditationTrack } from "./types";

/** Where what is running in the room came from. */
export enum MeetingSelectionKind {
  /** A meditation from the library: a script, a recording, or both. */
  MEDITATION = "meditation",
  /** A piece of the therapist's own music, played on its own. */
  TRACK = "track",
}

/**
 * What the meeting panel is running.
 *
 * A meditation is not the only thing a therapist puts on. Plenty of the work
 * is guided in their own words, live, with music under it — and for that a
 * written meditation is not a help but a thing to write first. So a track from
 * their own library is a first-class choice here: they press play and talk,
 * with no script to follow and nothing to prepare.
 *
 * Both shapes are driven by the same controls and reported to the patient in
 * the same packet, so the two sides never learn a second protocol.
 */
export type MeetingSelection =
  | { kind: MeetingSelectionKind.MEDITATION; meditation: Meditation }
  | { kind: MeetingSelectionKind.TRACK; track: MeditationTrack };

/** What the patient is told is playing. */
export function meetingSelectionTitle(selection: MeetingSelection): string {
  return selection.kind === MeetingSelectionKind.MEDITATION
    ? selection.meditation.title
    : selection.track.name;
}

/** The script to read from, where there is one. A track has none. */
export function meetingSelectionScript(
  selection: MeetingSelection,
): string | null {
  return selection.kind === MeetingSelectionKind.MEDITATION
    ? (selection.meditation.script ?? null)
    : null;
}

/**
 * How long it is expected to run, before anything is loaded.
 *
 * Only a hint: the stage reports the real length once the file has its
 * metadata, and a track uploaded before we measured lengths has none at all.
 */
export function meetingSelectionDuration(selection: MeetingSelection): number {
  if (selection.kind === MeetingSelectionKind.TRACK) {
    return selection.track.duration_seconds ?? 0;
  }
  const { meditation } = selection;
  return meditation.audio_duration_seconds ?? meditation.estimated_seconds;
}

/**
 * The packet that puts the patient's screen in the same state as this one.
 *
 * A track carries no background bed of its own: it *is* what is being heard,
 * so it is announced as the audio rather than as something under it.
 */
export function meetingSelectionState(
  selection: MeetingSelection,
  playing: boolean,
  positionSeconds: number,
): Omit<MeditationSessionState, "atEpochMs"> {
  return {
    meditationId:
      selection.kind === MeetingSelectionKind.MEDITATION
        ? selection.meditation.id
        : selection.track.id,
    title: meetingSelectionTitle(selection),
    hasAudio:
      selection.kind === MeetingSelectionKind.MEDITATION
        ? selection.meditation.has_audio
        : true,
    backgroundTrack:
      selection.kind === MeetingSelectionKind.MEDITATION
        ? selection.meditation.background_track
        : BackgroundTrackId.NONE,
    playing,
    positionSeconds,
    durationSeconds: meetingSelectionDuration(selection),
  };
}
